import { useCallback } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import { QueryKeys } from '@/constants/query-keys';
import { getRouteMap } from '@/utility';
import { mainLayoutRoutes } from '@/routes/config';
import type { Company } from '@gokrew/models/company';

const routeMap = getRouteMap(mainLayoutRoutes);

export function useCompanySwitch() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { companyId, changeCompany, handleSetBranch } = useAuth();

  return useCallback(
    (company: Company) => {
      if (company.id === companyId) return;
      handleSetBranch(null);
      queryClient.removeQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return key !== QueryKeys.Profile && key !== QueryKeys.Companies;
        },
      });
      // Static routes flagged redirectOnSwitch (e.g. "create" forms) redirect even on a literal path match.
      const literalEntry = routeMap.get(location.pathname);
      const entry =
        literalEntry?.redirectOnSwitch === true
          ? literalEntry
          : [...routeMap.entries()].find(
              ([pattern]) =>
                !routeMap.has(location.pathname) &&
                matchPath(pattern, location.pathname)
            )?.[1];
      if (entry) navigate(entry.redirectTo, { replace: true });
      changeCompany(company);
    },
    [
      companyId,
      handleSetBranch,
      queryClient,
      location.pathname,
      navigate,
      changeCompany,
    ]
  );
}
